import React, {useMemo, useState} from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    Stack,
    FormControl,
    FormHelperText,
    Tooltip,
    Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import instance from '../../../instance';
import CallNotification from '../../../context/NotificationContext';
import {ICreateMerchantRequest} from '../../../interfaces/fetch/CreateMerchantRequest';
import {getLabelWithAsterisk} from '../../../functionts/getLabelWithAsterisk';

interface RegisterMerchantPopupProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (newUserId: number) => void;
    serviceCenterId: number | null;
}

interface IFormControl {
    name: string;
    label: string;
    type?: string;
    required?: boolean;
    value: any;
    valid: boolean;
    touched: boolean;
    error?: string;
}

const RegisterMerchantPopup = ({open, onClose, onSuccess, serviceCenterId}: RegisterMerchantPopupProps) => {
    const {handleErrorNotification} = CallNotification();
    const [isSubmitting, setSubmitting] = useState(false);

    const [controls, setControls] = useState<IFormControl[]>([
        {name: 'name', label: 'Название организации', value: '', valid: true, touched: false, required: true},
        {name: 'inn', label: 'ИИН/БИН', value: '', valid: true, touched: false, required: true},
        {name: 'address', label: 'Адрес', value: '', valid: true, touched: false, required: true},
        {name: 'email', label: 'Email', value: '', valid: true, touched: false, required: true, type: 'email'},
        {name: 'phone', label: 'Телефон', value: '', valid: true, touched: false, required: true},
        {name: 'password', label: 'Пароль', type: 'password', value: '', valid: true, touched: false, required: true},
        {
            name: 'confirmPassword',
            label: 'Подтвердите пароль',
            type: 'password',
            value: '',
            valid: true,
            touched: false,
            required: true,
        },
        {
            name: 'vatPayer',
            label: 'Является плательщиком НДС',
            value: false,
            valid: true,
            touched: false,
            type: 'checkbox',
        },
    ]);

    const validateControl = (control: IFormControl): IFormControl => {
        let valid = true;
        let error = '';

        if (control.required && (control.type === 'checkbox' ? !control.value : !String(control.value).trim())) {
            valid = false;
            error = 'Поле обязательно';
        }

        if (control.name === 'inn' && !/^\d{12}$/.test(control.value)) {
            valid = false;
            error = 'Должно быть 12 цифр';
        }

        if (control.name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(control.value)) {
            valid = false;
            error = 'Неверный email';
        }

        if (control.name === 'phone' && !/^\+?\d{10,15}$/.test(control.value.replace(/\s/g, ''))) {
            valid = false;
            error = 'Введите корректный номер (10-15 цифр)';
        }

        if (control.name === 'password') {
            const val = String(control.value);
            if (val.length < 7) {
                valid = false;
                error = 'Мин. 7 символов';
            } else if (!/[a-zA-Z]/.test(val) || !/\d/.test(val) || !/[!-~]/.test(val)) {
                valid = false;
                error = 'Латинские буквы, цифры и спец. символы';
            }
        }

        if (control.name === 'confirmPassword') {
            const passwordValue = controls.find(c => c.name === 'password')?.value;
            if (control.value !== passwordValue) {
                valid = false;
                error = 'Пароли не совпадают';
            }
        }

        return {...control, valid, error, touched: true};
    };

    const handleChange = (name: string, value: any) => {
        setControls(prev => prev.map(control => (control.name === name ? validateControl({...control, value}) : control)));
    };

    const handleBlur = (name: string) => {
        setControls(prev => prev.map(control => (control.name === name ? validateControl({...control, touched: true}) : control)));
    };

    const isFormValid = useMemo(() => controls.every(c => !c.required || (c.valid && String(c.value).trim() !== '')), [controls]);

    const getValue = (name: string) => controls.find(c => c.name === name)?.value;

    const handleSubmit = async () => {
        if (!isFormValid || !serviceCenterId) return;

        setSubmitting(true);

        const request: ICreateMerchantRequest = {
            method: 'CreateMerchant',
            params: {
                e_mail: getValue('email'),
                phone: getValue('phone'),
                inn: getValue('inn'),
                name: getValue('name'),
                address: getValue('address'),
                flag_vat: getValue('vatPayer') ? 'Y' : 'N',
                service_center_id: serviceCenterId?.toString(),
                password: getValue('password'),
            },
        };

        try {
            const response = await instance.post('/LK', request);
            if (response.data.error === 0 && response.data.user_id) {
                onSuccess(response.data.user_id);
            } else {
                handleErrorNotification(response);
            }
        } catch (error) {
            handleErrorNotification(error, true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <div style={{padding: '20px 24px 10px 3px', position: 'relative', backgroundColor: '#f5f7ff'}}>
                <DialogTitle sx={{pr: 5}}>Регистрация нового мерчанта</DialogTitle>
                <IconButton onClick={onClose} style={{position: 'absolute', right: 16, top: 16}}>
                    <CloseIcon />
                </IconButton>
            </div>
            <DialogContent>
                <form
                    autoComplete="off"
                    onSubmit={e => {
                        e.preventDefault();
                        void handleSubmit();
                    }}
                >
                    <Stack spacing={2}>
                        {controls.map(control => (
                            <FormControl key={control.name} fullWidth error={!control.valid && control.touched} variant="standard">
                                {control.type === 'checkbox' ? (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(control.value)}
                                                onChange={e => handleChange(control.name, e.target.checked)}
                                                onBlur={() => handleBlur(control.name)}
                                            />
                                        }
                                        label={getLabelWithAsterisk(control.label, control.required)}
                                    />
                                ) : (
                                    <TextField
                                        type={control.type || 'text'}
                                        label={getLabelWithAsterisk(control.label, control.required)}
                                        value={control.value}
                                        onChange={e => handleChange(control.name, e.target.value)}
                                        onBlur={() => handleBlur(control.name)}
                                        autoComplete={
                                            control.name === 'password' || control.name === 'confirmPassword' ? 'new-password' : 'off'
                                        }
                                        error={!control.valid && control.touched}
                                        fullWidth
                                    />
                                )}
                                {control.error && control.touched && <FormHelperText>{control.error}</FormHelperText>}
                            </FormControl>
                        ))}

                        <Box sx={{mt: 4, mb: 4}}>
                            <Tooltip title="Заполните все обязательные поля" disableHoverListener={isFormValid}>
                                <span>
                                    <Button type="submit" sx={{mr: 2}} disabled={!isFormValid || isSubmitting}>
                                        Зарегистрировать
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button variant="text" onClick={onClose}>
                                Отмена
                            </Button>
                        </Box>
                    </Stack>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default RegisterMerchantPopup;
